// Premium Dashboard Data Hook
// Consolidates all dashboard data using real database records
// All calculations are dynamic - nothing is stored in database

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from './useAuth'
import { useProfile } from './useProfile'
import { 
  startOfMonth, 
  endOfMonth, 
  subMonths, 
  format, 
  getDate, 
  getDaysInMonth,
  eachDayOfInterval,
  parseISO
} from 'date-fns'

// ============================================
// Types
// ============================================

export interface DailyRevenue {
  date: string
  day: number
  revenue: number
  previousMonthRevenue: number
}

export interface ClientRevenue {
  clientId: string
  clientName: string
  revenue: number
  percentage: number
}

export interface HoursSplit {
  billable: number
  nonBillable: number
  billableValue: number
}

export interface SmartAlert {
  id: string
  type: 'success' | 'warning' | 'danger' | 'info'
  icon: string
  title: string
  message: string
  priority: number
}

export interface DashboardPremiumData {
  // Hero Section
  confirmedRevenue: number
  monthlyGoal: number
  goalProgress: number
  previousMonthRevenue: number
  revenueGrowthPercent: number
  dailyRevenueChart: DailyRevenue[]
  
  // Projection Section
  projectedRevenue: number
  expectedRevenue: number
  futureRecurringRevenue: number
  dailyAverage: number
  daysRemaining: number
  isOnTrack: boolean
  projectionMessage: string
  
  // Distribution Section
  clientRevenues: ClientRevenue[]
  hoursSplit: HoursSplit
  
  // Performance Metrics
  realHourlyRate: number
  targetHourlyRate: number
  totalMonthHours: number
  hoursGrowthPercent: number
  previousMonthHours: number
  
  // Alerts
  alerts: SmartAlert[]
  
  // Status
  isLoading: boolean
  activeProjects: number
  upcomingDeadlines: number
}

// ============================================
// Main Hook
// ============================================

export function useDashboardPremium(): DashboardPremiumData {
  const { user } = useAuth()
  const { data: profile } = useProfile()

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'premium', user?.id],
    queryFn: async () => {
      if (!user?.id) return null

      const today = new Date()
      const currentMonthStart = startOfMonth(today)
      const currentMonthEnd = endOfMonth(today)
      const previousMonthStart = startOfMonth(subMonths(today, 1))
      const previousMonthEnd = endOfMonth(subMonths(today, 1))

      // Parallel queries for efficiency
      const [
        currentMonthPaymentsResult,
        previousMonthPaymentsResult,
        currentMonthEntriesResult,
        previousMonthEntriesResult,
        pendingPaymentsResult,
        overduePaymentsResult,
        recurringPaymentsResult,
        activeProjectsResult,
        dailyPaymentsResult,
        previousDailyPaymentsResult
      ] = await Promise.all([
        // Current month paid payments
        supabase
          .from('payments')
          .select(`
            id, amount, status, paid_date, 
            projects!left(client_id, clients!left(id, name))
          `)
          .eq('status', 'paid')
          .gte('paid_date', format(currentMonthStart, 'yyyy-MM-dd'))
          .lte('paid_date', format(currentMonthEnd, 'yyyy-MM-dd')),

        // Previous month paid payments
        supabase
          .from('payments')
          .select('amount, paid_date')
          .eq('status', 'paid')
          .gte('paid_date', format(previousMonthStart, 'yyyy-MM-dd'))
          .lte('paid_date', format(previousMonthEnd, 'yyyy-MM-dd')),

        // Current month time entries (billable split)
        supabase
          .from('time_entries')
          .select('duration_minutes, is_billable, hourly_rate')
          .eq('user_id', user.id)
          .gte('start_time', currentMonthStart.toISOString())
          .lte('start_time', currentMonthEnd.toISOString()),

        // Previous month time entries
        supabase
          .from('time_entries')
          .select('duration_minutes')
          .eq('user_id', user.id)
          .gte('start_time', previousMonthStart.toISOString())
          .lte('start_time', previousMonthEnd.toISOString()),

        // Pending payments (expected revenue)
        supabase
          .from('payments')
          .select('amount, due_date')
          .eq('status', 'pending')
          .gte('due_date', format(currentMonthStart, 'yyyy-MM-dd'))
          .lte('due_date', format(currentMonthEnd, 'yyyy-MM-dd')),

        // Overdue payments
        supabase
          .from('payments')
          .select('id')
          .eq('status', 'overdue'),

        // Future recurring payments
        supabase
          .from('payments')
          .select('amount, recurring_day_of_month')
          .eq('status', 'pending')
          .eq('is_recurring', true)
          .not('recurring_day_of_month', 'is', null),

        // Active projects
        supabase
          .from('projects')
          .select('id, deadline')
          .eq('user_id', user.id)
          .in('status', ['backlog', 'in_progress', 'review']),

        // Daily payments for current month (for chart)
        supabase
          .from('payments')
          .select('amount, paid_date')
          .eq('status', 'paid')
          .gte('paid_date', format(currentMonthStart, 'yyyy-MM-dd'))
          .lte('paid_date', format(currentMonthEnd, 'yyyy-MM-dd')),

        // Previous month daily payments (for comparison chart)
        supabase
          .from('payments')
          .select('amount, paid_date')
          .eq('status', 'paid')
          .gte('paid_date', format(previousMonthStart, 'yyyy-MM-dd'))
          .lte('paid_date', format(previousMonthEnd, 'yyyy-MM-dd'))
      ])

      // Process current month revenue
      const currentMonthRevenue = currentMonthPaymentsResult.data?.reduce(
        (acc, p) => acc + (Number(p.amount) || 0), 0
      ) || 0

      // Process previous month revenue
      const previousMonthRevenue = previousMonthPaymentsResult.data?.reduce(
        (acc, p) => acc + (Number(p.amount) || 0), 0
      ) || 0

      // Process client revenues for donut chart
      const clientRevenueMap = new Map<string, { id: string; name: string; revenue: number }>()
      currentMonthPaymentsResult.data?.forEach((p: any) => {
        const clientId = p.projects?.client_id || 'no-client'
        const clientName = p.projects?.clients?.name || 'Sem cliente'
        const amount = Number(p.amount) || 0
        
        const existing = clientRevenueMap.get(clientId)
        if (existing) {
          existing.revenue += amount
        } else {
          clientRevenueMap.set(clientId, { id: clientId, name: clientName, revenue: amount })
        }
      })

      const clientRevenues = Array.from(clientRevenueMap.values())
        .map(c => ({
          clientId: c.id,
          clientName: c.name,
          revenue: c.revenue,
          percentage: currentMonthRevenue > 0 ? (c.revenue / currentMonthRevenue) * 100 : 0
        }))
        .sort((a, b) => b.revenue - a.revenue)

      // Process billable/non-billable split
      let billableMinutes = 0
      let nonBillableMinutes = 0
      let billableValue = 0
      const defaultRate = profile?.hourly_rate || 80

      currentMonthEntriesResult.data?.forEach(e => {
        const minutes = e.duration_minutes || 0
        if (e.is_billable !== false) {
          billableMinutes += minutes
          const rate = e.hourly_rate || defaultRate
          billableValue += (minutes / 60) * rate
        } else {
          nonBillableMinutes += minutes
        }
      })

      const previousMonthMinutes = previousMonthEntriesResult.data?.reduce(
        (acc, e) => acc + (e.duration_minutes || 0), 0
      ) || 0

      // Process expected revenue (pending payments)
      const expectedRevenue = pendingPaymentsResult.data?.reduce(
        (acc, p) => acc + (Number(p.amount) || 0), 0
      ) || 0

      // Process future recurring revenue
      const futureRecurringRevenue = recurringPaymentsResult.data?.reduce(
        (acc, p) => acc + (Number(p.amount) || 0), 0
      ) || 0

      // Process daily revenue for chart
      const daysInMonth = getDaysInMonth(today)
      const previousDaysInMonth = getDaysInMonth(subMonths(today, 1))
      
      const dailyRevenueMap = new Map<number, number>()
      dailyPaymentsResult.data?.forEach(p => {
        if (p.paid_date) {
          const day = parseISO(p.paid_date).getDate()
          dailyRevenueMap.set(day, (dailyRevenueMap.get(day) || 0) + (Number(p.amount) || 0))
        }
      })

      const previousDailyRevenueMap = new Map<number, number>()
      previousDailyPaymentsResult.data?.forEach(p => {
        if (p.paid_date) {
          const day = parseISO(p.paid_date).getDate()
          previousDailyRevenueMap.set(day, (previousDailyRevenueMap.get(day) || 0) + (Number(p.amount) || 0))
        }
      })

      const dailyRevenueChart: DailyRevenue[] = []
      for (let day = 1; day <= daysInMonth; day++) {
        dailyRevenueChart.push({
          date: format(new Date(today.getFullYear(), today.getMonth(), day), 'dd/MM'),
          day,
          revenue: dailyRevenueMap.get(day) || 0,
          previousMonthRevenue: previousDailyRevenueMap.get(Math.min(day, previousDaysInMonth)) || 0
        })
      }

      // Process active projects
      const activeProjects = activeProjectsResult.data?.length || 0
      const upcomingDeadlines = activeProjectsResult.data?.filter(p => {
        if (!p.deadline) return false
        const deadline = new Date(p.deadline)
        const daysUntil = Math.ceil((deadline.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
        return daysUntil >= 0 && daysUntil <= 7
      }).length || 0

      // Overdue count
      const overdueCount = overduePaymentsResult.data?.length || 0

      return {
        currentMonthRevenue,
        previousMonthRevenue,
        billableMinutes,
        nonBillableMinutes,
        billableValue,
        previousMonthMinutes,
        expectedRevenue,
        futureRecurringRevenue,
        clientRevenues,
        dailyRevenueChart,
        activeProjects,
        upcomingDeadlines,
        overdueCount
      }
    },
    enabled: !!user?.id,
    refetchInterval: 60000
  })

  // Compute all derived values
  return useMemo(() => {
    const defaultResult: DashboardPremiumData = {
      confirmedRevenue: 0,
      monthlyGoal: 10000,
      goalProgress: 0,
      previousMonthRevenue: 0,
      revenueGrowthPercent: 0,
      dailyRevenueChart: [],
      projectedRevenue: 0,
      expectedRevenue: 0,
      futureRecurringRevenue: 0,
      dailyAverage: 0,
      daysRemaining: 0,
      isOnTrack: false,
      projectionMessage: '',
      clientRevenues: [],
      hoursSplit: { billable: 0, nonBillable: 0, billableValue: 0 },
      realHourlyRate: 0,
      targetHourlyRate: 80,
      totalMonthHours: 0,
      hoursGrowthPercent: 0,
      previousMonthHours: 0,
      alerts: [],
      isLoading,
      activeProjects: 0,
      upcomingDeadlines: 0
    }

    if (!data || !profile) return defaultResult

    const today = new Date()
    const currentDay = getDate(today)
    const daysInMonth = getDaysInMonth(today)
    const daysRemaining = daysInMonth - currentDay

    const monthlyGoal = profile.monthly_goal || 10000
    const targetHourlyRate = profile.hourly_rate || 80

    // Calculate goal progress
    const goalProgress = monthlyGoal > 0 ? Math.min((data.currentMonthRevenue / monthlyGoal) * 100, 100) : 0

    // Calculate revenue growth
    const revenueGrowthPercent = data.previousMonthRevenue > 0
      ? ((data.currentMonthRevenue - data.previousMonthRevenue) / data.previousMonthRevenue) * 100
      : 0

    // Calculate projection
    const dailyAverage = currentDay > 0 ? data.currentMonthRevenue / currentDay : 0
    const projectedRevenue = data.currentMonthRevenue + data.expectedRevenue + (dailyAverage * daysRemaining)
    const isOnTrack = projectedRevenue >= monthlyGoal
    const gap = Math.max(0, monthlyGoal - projectedRevenue)

    let projectionMessage: string
    if (isOnTrack) {
      projectionMessage = `Se continuar neste ritmo, você fechará o mês em R$ ${projectedRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    } else {
      const dailyNeeded = daysRemaining > 0 ? gap / daysRemaining : gap
      projectionMessage = `Para atingir a meta, você precisa faturar R$ ${dailyNeeded.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} por dia`
    }

    // Calculate hours metrics
    const billableHours = data.billableMinutes / 60
    const nonBillableHours = data.nonBillableMinutes / 60
    const totalMonthHours = billableHours + nonBillableHours
    const previousMonthHours = data.previousMonthMinutes / 60

    const hoursGrowthPercent = previousMonthHours > 0
      ? ((totalMonthHours - previousMonthHours) / previousMonthHours) * 100
      : 0

    // Calculate real hourly rate
    const realHourlyRate = billableHours > 0 ? data.currentMonthRevenue / billableHours : 0

    // Generate intelligent alerts
    const alerts: SmartAlert[] = []

    // Alert: Overdue payments
    if (data.overdueCount > 0) {
      alerts.push({
        id: 'overdue',
        type: 'danger',
        icon: '🔴',
        title: 'Pagamentos atrasados',
        message: `Você tem ${data.overdueCount} pagamento(s) em atraso`,
        priority: 1
      })
    }

    // Alert: Revenue below goal
    if (!isOnTrack && gap > 0) {
      const percentBelow = Math.round((gap / monthlyGoal) * 100)
      alerts.push({
        id: 'goal-risk',
        type: 'warning',
        icon: '📉',
        title: 'Meta em risco',
        message: `Você está ${percentBelow}% abaixo da meta. Faltam R$ ${gap.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
        priority: 2
      })
    }

    // Alert: Client dependency (>60%)
    const highDependencyClient = data.clientRevenues.find(c => c.percentage > 60)
    if (highDependencyClient) {
      alerts.push({
        id: 'dependency',
        type: 'danger',
        icon: '⚠️',
        title: 'Alto risco de dependência',
        message: `Cliente "${highDependencyClient.clientName}" representa ${Math.round(highDependencyClient.percentage)}% da receita`,
        priority: 3
      })
    }

    // Alert: Real hourly rate below target
    if (billableHours > 10 && realHourlyRate < targetHourlyRate * 0.9) {
      const percentBelow = Math.round((1 - realHourlyRate / targetHourlyRate) * 100)
      alerts.push({
        id: 'rate-below',
        type: 'warning',
        icon: '💰',
        title: 'Valor/hora abaixo do ideal',
        message: `Seu valor real (R$ ${realHourlyRate.toFixed(2)}/h) está ${percentBelow}% abaixo da meta`,
        priority: 4
      })
    }

    // Alert: High non-billable hours (>20%)
    const nonBillablePercent = totalMonthHours > 0 ? (nonBillableHours / totalMonthHours) * 100 : 0
    if (totalMonthHours > 20 && nonBillablePercent > 20) {
      alerts.push({
        id: 'non-billable',
        type: 'info',
        icon: '⏱️',
        title: 'Tempo não faturável alto',
        message: `${Math.round(nonBillablePercent)}% do seu tempo é dedicado a tarefas não faturáveis`,
        priority: 5
      })
    }

    // Alert: Goal achieved
    if (goalProgress >= 100) {
      alerts.push({
        id: 'goal-achieved',
        type: 'success',
        icon: '🎉',
        title: 'Meta atingida!',
        message: `Parabéns! Você atingiu ${Math.round(goalProgress)}% da meta mensal`,
        priority: 6
      })
    }

    return {
      confirmedRevenue: data.currentMonthRevenue,
      monthlyGoal,
      goalProgress,
      previousMonthRevenue: data.previousMonthRevenue,
      revenueGrowthPercent,
      dailyRevenueChart: data.dailyRevenueChart,
      projectedRevenue,
      expectedRevenue: data.expectedRevenue,
      futureRecurringRevenue: data.futureRecurringRevenue,
      dailyAverage,
      daysRemaining,
      isOnTrack,
      projectionMessage,
      clientRevenues: data.clientRevenues,
      hoursSplit: {
        billable: billableHours,
        nonBillable: nonBillableHours,
        billableValue: data.billableValue
      },
      realHourlyRate,
      targetHourlyRate,
      totalMonthHours,
      hoursGrowthPercent,
      previousMonthHours,
      alerts: alerts.sort((a, b) => a.priority - b.priority).slice(0, 3),
      isLoading,
      activeProjects: data.activeProjects,
      upcomingDeadlines: data.upcomingDeadlines
    }
  }, [data, profile, isLoading])
}
